/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import {
  Bar,
  BarChart,
  XAxis,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'
import usePersistedState from '../../hooks/usePersistedState'
import { formatValue } from '../../utils/formatValues'
import { Valor } from '../../utils/masks'
import InfoBox from '../../components/InfoBox'
import Header from '../../components/Header'
import Button from '../../components/Button'
import { UserDetails } from '../../utils/interfaces'
import { Container, Content, Cards, GraphWrapper } from './styles'

interface Values {
  year: number
  invested: number
  finalBalance: number
  rentability: number
}

const Simulation: React.FC = () => {
  const [userDetails] = usePersistedState<UserDetails>(
    'userDetails',
    {} as UserDetails,
  )
  const history = useHistory()
  const [, setValues] = useState<Values[]>([])
  const [, setValuesCDI] = useState<Values[]>([])
  const [, setValuesPou] = useState<Values[]>([])
  const [totalInvested, setTotalInvested] = useState(0)
  const [totalBalance, setTotalBalance] = usePersistedState('totalBalance', 0)
  const [, setTotalInvestedCDI] = useState(0)
  const [totalBalanceCDI, setTotalBalanceCDI] = useState(0)
  const [, setTotalInvestedPoup] = useState(0)
  const [totalBalancePoup, setTotalBalancePoup] = useState(0)
  const [launched, setLaunched] = useState(false)
  const [lastDate, setLastDate] = useState('')
  const [, setTotalRentability] = usePersistedState('totalRentability', 0)
  const colors = ['#97a19d', '#97a19d', '#4CA585']

  const { contribuicaoBasica, aport, years } = userDetails
  const actualYear = new Date().getFullYear()
  const lastYear = actualYear + parseInt(years.toString(), 10)

  useEffect(() => {
    setLastDate(`${moment().get('date')}/${moment().get('month')}/${lastYear}`)
    setTotalRentability((totalBalance * 100) / totalInvested)
  }, [totalInvested, totalBalance, setTotalRentability, lastYear])

  const createValues = useCallback(() => {
    const valuesCalculated: Values[] = []
    let totalInvestedTemp = totalInvested
    let temporaryFinalBalance = 0 + aport

    for (let i = actualYear; i < lastYear; i += 1) {
      const rentability = 0.33461 // 4.09 anual
      let finalBalance = 0

      for (let j = 1; j <= 12; j += 1) {
        const monthinvested = contribuicaoBasica
        totalInvestedTemp += monthinvested
        finalBalance =
          temporaryFinalBalance +
          monthinvested +
          (temporaryFinalBalance + monthinvested) * (rentability / 100)

        temporaryFinalBalance = finalBalance
      }
      const val: Values = {
        year: i,
        invested: totalInvestedTemp,
        rentability,
        finalBalance,
      }
      valuesCalculated.push(val)
    }
    setTotalInvested(totalInvestedTemp)
    setTotalBalance(temporaryFinalBalance)
    setValues(valuesCalculated)
    setLaunched(true)
  }, [
    contribuicaoBasica,
    totalInvested,
    aport,
    setTotalBalance,
    actualYear,
    lastYear,
  ])

  const createValuesCDI = useCallback(() => {
    const valuesCalculated: Values[] = []
    let totalInvestedTemp = totalInvested
    let temporaryFinalBalance = 0 + aport

    for (let i = actualYear; i < lastYear; i += 1) {
      const rentability = 0.22633 // 2.75 anual
      let finalBalance = 0

      for (let j = 1; j <= 12; j += 1) {
        const monthinvested = contribuicaoBasica
        totalInvestedTemp += monthinvested
        finalBalance =
          temporaryFinalBalance +
          monthinvested +
          (temporaryFinalBalance + monthinvested) * (rentability / 100)

        temporaryFinalBalance = finalBalance
      }
      const val: Values = {
        year: i,
        invested: totalInvestedTemp,
        rentability,
        finalBalance,
      }

      valuesCalculated.push(val)
    }
    setTotalInvestedCDI(totalInvestedTemp)
    setTotalBalanceCDI(temporaryFinalBalance)
    setValuesCDI(valuesCalculated)
    setLaunched(true)
  }, [actualYear, aport, contribuicaoBasica, lastYear, totalInvested])

  const createValuesPou = useCallback(() => {
    const valuesCalculated: Values[] = []
    let totalInvestedTemp = totalInvested
    let temporaryFinalBalance = 0 + aport

    for (let i = actualYear; i < lastYear; i += 1) {
      const rentability = 0.15861 // 1.92 anual
      let finalBalance = 0

      for (let j = 1; j <= 12; j += 1) {
        const monthinvested = contribuicaoBasica
        totalInvestedTemp += monthinvested
        finalBalance =
          temporaryFinalBalance +
          monthinvested +
          (temporaryFinalBalance + monthinvested) * (rentability / 100)

        temporaryFinalBalance = finalBalance
      }
      const val: Values = {
        year: i,
        invested: totalInvestedTemp,
        rentability,
        finalBalance,
      }

      valuesCalculated.push(val)
    }

    setTotalInvestedPoup(totalInvestedTemp)
    setTotalBalancePoup(temporaryFinalBalance)
    setValuesPou(valuesCalculated)
    setLaunched(true)
  }, [actualYear, aport, contribuicaoBasica, lastYear, totalInvested])

  const handleChangeValues = useCallback(() => {
    history.push('/')
  }, [history])

  const handleConfirmValues = useCallback(() => {
    history.push('/result-simulation')
  }, [history])

  const data = [
    {
      name: 'Poupança',
      rendimento: totalBalancePoup.toFixed(2),
    },
    {
      name: 'CDI',
      rendimento: totalBalanceCDI.toFixed(2),
    },
    {
      name: 'Familia Ceres',
      rendimento: totalBalance.toFixed(2),
    },
  ]

  useEffect(() => {
    if (!launched) {
      createValues()
      createValuesCDI()
      createValuesPou()
    }
  }, [createValues, createValuesCDI, createValuesPou, launched])

  return (
    <>
      <Header>
        <FiArrowLeft
          type="button"
          cursor="pointer"
          onClick={handleChangeValues}
        />
      </Header>
      <Container>
        <Content>
          <h3>
            Veja como o seu esforço será <strong>recompensado</strong>
          </h3>
          <Cards>
            <InfoBox>
              <h3>Seu investimento no período</h3>
              <span>{formatValue(totalInvested + userDetails.aport)}</span>
            </InfoBox>
            <InfoBox>
              <h3>Seu saldo em {lastDate}</h3>
              <span>{formatValue(totalBalance)}</span>
            </InfoBox>
          </Cards>

          <strong>Os dados não mentem.</strong>

          <GraphWrapper>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={250}
                margin={{
                  top: 25,
                  right: 20,
                  left: 20,
                  bottom: 5,
                }}
                data={data}
              >
                <XAxis dataKey="name" />
                <Bar
                  dataKey="rendimento"
                  barSize={90}
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % 20]} />
                  ))}
                  <LabelList
                    dataKey="rendimento"
                    position="top"
                    formatter={(number: string) => Valor(number)}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GraphWrapper>

          <small>
            Os valores são simulações a partir de uma rentabilidade de 4% e não
            há garantia de rentabilidade futura.
          </small>

          <Button type="button" color="green" onClick={handleConfirmValues}>
            Pronto, é isso que eu quero
            <div>
              <FiArrowRight />
            </div>
          </Button>

          <Button
            type="button"
            color="transparent"
            onClick={handleChangeValues}
          >
            <div>
              <FiArrowLeft />
            </div>
            Quero simular com outros valores
          </Button>
        </Content>
      </Container>
    </>
  )
}

export default Simulation
